import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Botao, Cartao, Esqueleto, TituloSecao } from '@/componentes/ui'
import { formatarData } from '@/lib/datas'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utilitarios'
import type { Ciclo, DiaPrograma } from '@/tipos/tabelas'
import { CHAVES_ADMIN, useProgramaAtual } from './comum'

/**
 * Títulos e frases inspiradoras dos 28 dias.
 *
 * O seed criou os dias com o título provisório "Dia N" — esta tela é
 * onde eles ganham conteúdo de verdade. A frase aparece na tela inicial,
 * logo abaixo da saudação.
 */
export function SecaoDias() {
  const { data: programa } = useProgramaAtual()
  const clienteQuery = useQueryClient()
  const [emEdicao, setEmEdicao] = useState<string | null>(null)
  const [titulo, setTitulo] = useState('')
  const [frase, setFrase] = useState('')
  const [liberadoEm, setLiberadoEm] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: CHAVES_ADMIN.dias(programa?.id ?? ''),
    queryFn: async () => {
      const [{ data: dias }, { data: ciclos }] = await Promise.all([
        supabase
          .from('dias_programa')
          .select('*')
          .eq('programa_id', programa?.id ?? '')
          .order('numero'),
        supabase
          .from('ciclos')
          .select('*')
          .eq('programa_id', programa?.id ?? '')
          .order('numero'),
      ])
      return { dias: (dias ?? []) as DiaPrograma[], ciclos: (ciclos ?? []) as Ciclo[] }
    },
    enabled: Boolean(programa),
  })

  const salvar = useMutation({
    mutationFn: async (diaId: string) => {
      const { error } = await supabase
        .from('dias_programa')
        .update({
          titulo: titulo.trim(),
          frase_inspiradora: frase.trim() || null,
          // Vazio volta a null: o dia segue o ritmo normal do programa.
          liberado_em: liberadoEm ? new Date(liberadoEm).toISOString() : null,
        })
        .eq('id', diaId)
      if (error) throw error
    },
    onSuccess: () => {
      setEmEdicao(null)
      void clienteQuery.invalidateQueries({ queryKey: CHAVES_ADMIN.dias(programa?.id ?? '') })
    },
  })

  function abrirEdicao(dia: DiaPrograma) {
    setEmEdicao(dia.id)
    setTitulo(dia.titulo)
    setFrase(dia.frase_inspiradora ?? '')
    // O input datetime-local quer `YYYY-MM-DDTHH:mm`, sem fuso.
    setLiberadoEm(dia.liberado_em ? dia.liberado_em.slice(0, 16) : '')
  }

  if (isLoading) return <Esqueleto className="h-64" />
  if (!data) return null

  const semConteudo = data.dias.filter((d) => /^Dia \d+$/.test(d.titulo)).length

  return (
    <div className="flex flex-col gap-6">
      {semConteudo > 0 && (
        <Cartao className="bg-dourado/15 text-sm leading-relaxed text-[var(--tinta-suave)]">
          <strong className="font-titulo font-semibold text-[var(--tinta)]">
            {semConteudo} de {data.dias.length} dias
          </strong>{' '}
          ainda estão com o título provisório. A frase inspiradora aparece na tela inicial da
          participante.
        </Cartao>
      )}

      {data.ciclos.map((ciclo) => (
        <section key={ciclo.id}>
          <TituloSecao>
            Ciclo {ciclo.numero} — {ciclo.nome}
          </TituloSecao>
          <p className="mb-3 -mt-2 text-sm text-[var(--tinta-apagada)]">{ciclo.objetivo}</p>

          <div className="flex flex-col gap-2">
            {data.dias
              .filter((d) => d.numero >= ciclo.dia_inicio && d.numero <= ciclo.dia_fim)
              .map((dia) => {
                const editando = emEdicao === dia.id
                const provisorio = /^Dia \d+$/.test(dia.titulo)

                return (
                  <Cartao key={dia.id} className="p-4">
                    {editando ? (
                      <div className="flex flex-col gap-3">
                        <input
                          type="text"
                          value={titulo}
                          onChange={(e) => setTitulo(e.target.value)}
                          placeholder={`Título do Dia ${dia.numero}`}
                          className="h-12 w-full rounded-xl border-2 border-[var(--borda)] bg-[var(--fundo)] px-3 font-titulo text-[15px] font-semibold"
                        />
                        <textarea
                          rows={2}
                          value={frase}
                          onChange={(e) => setFrase(e.target.value)}
                          placeholder="Frase inspiradora (opcional)"
                          className="w-full resize-none rounded-xl border-2 border-[var(--borda)] bg-[var(--fundo)] p-3 text-sm"
                        />

                        <label className="block">
                          <span className="mb-1 block text-xs font-medium text-[var(--tinta-suave)]">
                            Liberar a partir de (opcional)
                          </span>
                          <input
                            type="datetime-local"
                            value={liberadoEm}
                            onChange={(e) => setLiberadoEm(e.target.value)}
                            className="h-11 w-full rounded-xl border-2 border-[var(--borda)] bg-[var(--fundo)] px-3 text-sm"
                          />
                          <span className="mt-1 block text-xs text-[var(--tinta-apagada)]">
                            Em branco, o dia segue o ritmo normal do programa. Com data, só aparece
                            a partir dela mesmo que a participante já esteja neste dia.
                          </span>
                        </label>

                        <div className="flex gap-2">
                          <Botao
                            tamanho="pequeno"
                            carregando={salvar.isPending}
                            onClick={() => salvar.mutate(dia.id)}
                          >
                            Salvar
                          </Botao>
                          <Botao
                            variante="fantasma"
                            tamanho="pequeno"
                            onClick={() => setEmEdicao(null)}
                          >
                            Cancelar
                          </Botao>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => abrirEdicao(dia)}
                        className="flex w-full items-start gap-3 text-left"
                      >
                        <span
                          className={cn(
                            'flex size-9 shrink-0 items-center justify-center rounded-full font-titulo text-sm font-bold',
                            provisorio
                              ? 'bg-[var(--borda)] text-[var(--tinta-apagada)]'
                              : 'bg-coral text-white',
                          )}
                        >
                          {dia.numero}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span
                            className={cn(
                              'block font-titulo text-[15px] font-semibold',
                              provisorio && 'text-[var(--tinta-apagada)]',
                            )}
                          >
                            {dia.titulo}
                          </span>
                          {dia.frase_inspiradora && (
                            <span className="mt-0.5 block text-sm text-[var(--tinta-suave)] italic">
                              {dia.frase_inspiradora}
                            </span>
                          )}
                          {dia.liberado_em && (
                            <span className="mt-1 inline-block rounded-full bg-dourado/25 px-2 py-0.5 text-[11px] font-medium text-marrom">
                              🔒 libera em {formatarData(dia.liberado_em, "d/MM 'às' HH:mm")}
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 text-[var(--tinta-apagada)]" aria-hidden>
                          ✎
                        </span>
                      </button>
                    )}
                  </Cartao>
                )
              })}
          </div>
        </section>
      ))}
    </div>
  )
}
